import type { ModuleSlug } from '@/styles/tokens';
import type { UserSummary } from '@/auth/api';
import { canAccessAppraisals } from '@/auth/visibility';
import {
  canAccessCoportalRoute,
  coportalSlugFromPath,
  isCoportalAliasRoute,
  isGatedCoportalPage,
} from '@/auth/coportalPages';
import type { NavItem } from './moduleNav';
import { MODULE_TILE } from './modules';
import { currentRegistries } from '@/shared/modules/store';

/**
 * May this user see (and open) one `MODULE_NAV` item?
 *
 * One predicate for the two surfaces that list nav pages — the module rail
 * and the ⌘K launcher — so they can never disagree about what a user may
 * open. It is exactly the rule the route guards enforce:
 *
 *   · `requiresTile` — the item's own tile (HR ▸ Cash claims inside HR);
 *   · `requiresAppraisals` — HR ▸ Appraisals, mounted OUTSIDE the HR tile
 *     guard, so it is judged on appraisal access alone and survives a dark
 *     `hr` tile;
 *   · CS/OPS Portal pages — the bare-slug page grants, through the same
 *     `canAccessCoportalRoute` that `RequireCoportalPage` checks.
 *
 * `checkModuleTile` adds the module's own tile (`MODULE_TILE`, the map
 * `RequireTile` reads). The rail skips it — you are already inside the
 * module — but the launcher indexes every module at once and must not offer
 * a row whose route answers with /forbidden.
 */
export function isNavItemVisible(
  mod: ModuleSlug,
  item: NavItem,
  user: UserSummary | null,
  canSeeTile: (slug: string) => boolean,
  { checkModuleTile = false }: { checkModuleTile?: boolean } = {},
): boolean {
  if (item.requiresAppraisals) return canAccessAppraisals(user);
  if (checkModuleTile && !canSeeTile(MODULE_TILE[mod] ?? currentRegistries().tiles[mod])) return false;
  if (item.requiresTile && !canSeeTile(item.requiresTile)) return false;
  // CS/OPS pages are judged by their route, whichever module's rail shows
  // them (Guest Portal ▸ Content borrows a few; `coportalSlugFromPath` is
  // null for every other route).
  const slug = coportalSlugFromPath(item.to);
  if (slug) {
    if (
      (isGatedCoportalPage(slug) || isCoportalAliasRoute(slug)) &&
      !canAccessCoportalRoute(slug, user)
    ) {
      return false;
    }
  }
  return true;
}
